import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col } from "antd/es/grid";
import Popconfirm from "antd/es/popconfirm";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypesSupportingDownloading } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Button, CardCollapse, CollapsePanel, Modal } from "@mapmycustomers/ui";

import ColumnsMenu from "@app/component/SelectColumnsPane/ColumnsMenu";
import { MAX_ITEMS_TO_DOWNLOAD_FILE } from "@app/store/exportEntities/const";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import { UniversalFieldName } from "@app/util/fieldModel/universalFieldsFieldModel";

import localSettings from "../../config/LocalSettings";
import layout from "../../styles/layout";
import getFieldModelByEntityType from "../../util/fieldModel/getByEntityType";
import ColumnSelector from "../input/ColumnSelector/ColumnSelector";

import styles from "./DownloadEntitiesModal.module.scss";
import Info from "./Info";

const addSpecialExportFields = (
  entityType: EntityTypesSupportingDownloading,
  columns: ColumnModel<IField>
): ColumnModel<IField> => {
  const fieldModel = getFieldModelByEntityType(entityType);

  const names = new Set(columns.filter(({ visible }) => visible).map(({ field }) => field.name));
  const result: IField["name"][] = [];
  if (names.has(PersonFieldName.COMPANIES)) {
    if (!names.has(PersonFieldName.COMPANIES_IMPORT_ID)) {
      result.push(PersonFieldName.COMPANIES_IMPORT_ID);
    }
  } else if (names.has("account")) {
    if (!names.has("account.importId")) {
      result.push("account.importId");
    }
  }

  if (names.has("contact") && !names.has("contact.importId")) {
    result.push("contact.importId");
  }
  if (names.has(ActivityFieldName.DEAL) && !names.has(ActivityFieldName.DEAL_IMPORT_ID)) {
    result.push(ActivityFieldName.DEAL_IMPORT_ID);
  }
  if (
    names.has(CompanyFieldName.PARENT_COMPANY) &&
    !names.has(CompanyFieldName.PARENT_COMPANY_IMPORT_ID)
  ) {
    result.push(CompanyFieldName.PARENT_COMPANY_IMPORT_ID);
  }
  if (names.has(UniversalFieldName.LAST_ACTIVITY_DATE)) {
    if (!names.has(UniversalFieldName.LAST_ACTIVITY_NAME)) {
      result.push(UniversalFieldName.LAST_ACTIVITY_NAME);
    }
    if (!names.has(UniversalFieldName.LAST_ACTIVITY_TYPE)) {
      result.push(UniversalFieldName.LAST_ACTIVITY_TYPE);
    }
  }

  if (names.has("ownerName") && !names.has("user.username")) {
    result.push("user.username");
  }

  return [
    ...columns,
    ...result
      .map((fieldName) => fieldModel.getByName(fieldName))
      .filter(isDefined)
      .map((field) => ({ field, visible: true })),
  ];
};

const onlyShowExportableColumns = (field: IField) =>
  !field.hasFeature(FieldFeature.NON_EXPORT_VIEW);

export interface DownloadEntitiesProps {
  configurationKey?: string;
  defaultColumnList?: ColumnModel<IField>;
  entityType: EntityTypesSupportingDownloading;
  hasAdditionalFilters?: boolean;
  hideInformation?: boolean;
  informationPoint?: React.ReactNode;
  onDownload: (columns: ColumnModel<IField>) => void;
  onHide: () => void;
  total?: number;
}

const DownloadEntitiesModal: React.FC<DownloadEntitiesProps> = ({
  configurationKey,
  defaultColumnList,
  entityType,
  hasAdditionalFilters,
  hideInformation,
  informationPoint,
  onDownload,
  onHide,
  total,
}) => {
  const analytics = useExtendedAnalytics("Export");
  useEffect(() => {
    analytics.initiated("");
  }, [analytics]);

  const intl = useIntl();
  const configurationKeyToSave = `${entityType}${configurationKey ?? ""}`;
  const savedConfiguration: IField["name"][] | undefined = useMemo(() => {
    return localSettings.getExportEntityFields(configurationKeyToSave);
  }, [configurationKeyToSave]);

  const [columns, setColumns] = useState<ColumnModel<IField>>(() => {
    const columns =
      defaultColumnList ??
      getFieldModelByEntityType(entityType)
        .fields.filter((field) => !savedConfiguration || savedConfiguration.includes(field.name))
        .map((field) => ({ field, visible: true }));
    return addSpecialExportFields(entityType, columns);
  });
  const [columnVisible, setColumnVisibility] = useState<boolean>(!defaultColumnList);

  const entityCountReachedLimit = total && total > MAX_ITEMS_TO_DOWNLOAD_FILE;

  const handleDownload = useCallback(() => {
    analytics.completed("");
    localSettings.setExportEntityFields(
      configurationKeyToSave,
      columns.filter(({ visible }) => visible).map(({ field: { name } }) => name)
    );
    onDownload(columns);
    onHide();
  }, [analytics, columns, configurationKeyToSave, onHide, onDownload]);

  const confirmButton = useMemo(
    () => (
      <Button
        onClick={entityCountReachedLimit ? undefined : handleDownload}
        rightIcon={<FontAwesomeIcon icon={faDownload} />}
        type="primary"
      >
        {intl.formatMessage({
          id: "downloadEntities.modal.download",
          defaultMessage: "Download",
          description: "Download entities modal - Download",
        })}
      </Button>
    ),
    [entityCountReachedLimit, intl, handleDownload]
  );

  const handleCancelClick = useCallback(() => {
    analytics.cancelled("");
    onHide();
  }, [analytics, onHide]);

  return (
    <Modal
      centered
      footer={
        <Row gutter={layout.spacerM} justify="end">
          <Col>
            <Button onClick={handleCancelClick}>
              {intl.formatMessage({
                id: "downloadEntities.modal.cancel",
                defaultMessage: "Cancel",
                description: "Download entities modal - cancel",
              })}
            </Button>
          </Col>
          <Col>
            {entityCountReachedLimit ? (
              <Popconfirm
                cancelButtonProps={{ className: styles.cancelButton }}
                okText={intl.formatMessage({
                  id: "downloadEntities.modal.confirmation.ok",
                  defaultMessage: "Okay",
                  description: "Download entities modal - confirmation ok",
                })}
                onConfirm={handleDownload}
                placement="right"
                title={
                  <div className={styles.confirmation}>
                    {intl.formatMessage(
                      {
                        id: "downloadEntities.modal.confirmation.title",
                        defaultMessage:
                          "This download contains more than {count} records and will be sent to your email.",
                        description: "Download entities modal - confirmation title",
                      },
                      {
                        count: intl.formatNumber(MAX_ITEMS_TO_DOWNLOAD_FILE, {
                          notation: "compact",
                        }),
                      }
                    )}
                  </div>
                }
              >
                {confirmButton}
              </Popconfirm>
            ) : (
              confirmButton
            )}
          </Col>
        </Row>
      }
      onCancel={onHide}
      open
      title={intl.formatMessage({
        id: "downloadEntities.modal.title",
        defaultMessage: "Confirm Download",
        description: "Download entities modal - title",
      })}
    >
      <div className={styles.container}>
        {!hideInformation && (
          <Info hasAdditionalFilters={hasAdditionalFilters} informationPoint={informationPoint} />
        )}
        <CardCollapse
          accordion
          activeKey={columnVisible ? "collapse" : undefined}
          className={styles.collapseContainer}
          onChange={(key) => setColumnVisibility(!!key)}
          size="small"
        >
          <CollapsePanel
            header={
              <div className={styles.collapseHeader}>
                <ColumnsMenu columns={columns} entityType={entityType} />
                {intl.formatMessage({
                  id: "downloadEntities.modal.collapse.text",
                  defaultMessage: "Select Columns",
                  description: "Download entities modal - collapse title",
                })}
              </div>
            }
            key="collapse"
          >
            <ColumnSelector
              columns={columns}
              columnTreeClassName={styles.columnTree}
              entityType={entityType}
              fieldFilter={onlyShowExportableColumns}
              onUpdate={setColumns}
              showSelectAllLabel
            />
          </CollapsePanel>
        </CardCollapse>
      </div>
    </Modal>
  );
};

export default DownloadEntitiesModal;
